<template>
    <div class="project-data-manager">
        <div v-if="isLoading" class="loading-state">
            <i class="pi pi-spin pi-spinner loading-icon"></i>
            <div class="loading-text">Загружаем данные проекта...</div>
        </div>

        <!-- No data source selected yet -->
        <DataSourceSelector
            v-else-if="!selectedDataSource"
            :project="project"
            @source-selected="onSourceSelected"
            @source-needs-confirmation="onSourceNeedsConfirmation"
        />

        <!-- Manual mode -->
        <DataManualMode
            v-else-if="selectedDataSource === 'manual'"
            :project="project"
            :initial-data="projectData"
            @change-source="onChangeSource"
            @data-changed="reload"
            @needs-confirmation="onUploadNeedsConfirmation"
        />

        <!-- API mode -->
        <DataApiMode
            v-else-if="selectedDataSource === 'api'"
            :project="project"
            :initial-data="projectData"
            @change-source="onChangeSource"
            @data-changed="reload"
            @needs-confirmation="onUploadNeedsConfirmation"
        />

        <!-- Confirmation dialog -->
        <DataReplaceConfirm
            v-model:visible="showConfirmDialog"
            :row-count="confirmData.rowCount"
            :action="confirmData.action"
            @confirm="onConfirmAction"
            @cancel="onCancelAction"
        />
    </div>
</template>

<script>
import DataSourceSelector from './data/DataSourceSelector.vue';
import DataManualMode from './data/DataManualMode.vue';
import DataApiMode from './data/DataApiMode.vue';
import DataReplaceConfirm from './data/DataReplaceConfirm.vue';
import api from "@/libs/api";

export default {
    name: "ProjectDataManager",
    components: {
        DataSourceSelector,
        DataManualMode,
        DataApiMode,
        DataReplaceConfirm
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    emits: ['source-changed', 'data-source-changed'],
    data() {
        return {
            selectedDataSource: this.project.data_source,
            projectData: {
                keys: [],
                values: [],
                apiKey: null,
                rowCount: 0
            },
            isLoading: false,
            showConfirmDialog: false,
            confirmData: {
                rowCount: 0,
                action: 'upload',
                pendingSource: null,
                pendingFile: null,
                pendingFields: null
            }
        };
    },
    watch: {
        'project.data_source': {
            handler(value) {
                this.selectedDataSource = value;
                this.loadData();
            },
            immediate: true
        }
    },
    methods: {
        async loadData() {
            if (!this.selectedDataSource) {
                this.clearProjectData();
                this.isLoading = false;
                return;
            }

            this.isLoading = true;

            try {
                const data = await api.viewProjectData(this.project.id);
                this.projectData = {
                    keys: data.keys || [],
                    values: data.values || [],
                    apiKey: data.apiKey || null,
                    rowCount: data.rowCount || 0
                };
            } catch (err) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Ошибка',
                    detail: 'Не удалось загрузить данные проекта',
                    life: 5000
                });
            } finally {
                this.isLoading = false;
            }
        },
        reload() {
            this.loadData();
        },
        async onSourceSelected(source, errorData) {
            if (errorData && errorData.error === 'data_replacement_requires_confirmation') {
                this.confirmData = {
                    rowCount: errorData.current_row_count,
                    action: 'change_mode',
                    pendingSource: source,
                    pendingFile: null,
                    pendingFields: null
                };
                this.showConfirmDialog = true;
            } else {
                this.selectedDataSource = source;
                this.$emit('data-source-changed', source);
                this.$toast.add({
                    severity: 'success',
                    summary: 'Сохранено',
                    detail: 'Режим работы с данными установлен',
                    life: 2000
                });
                this.loadData();
            }
        },
        onSourceNeedsConfirmation(data) {
            this.confirmData = {
                rowCount: data.currentRowCount,
                action: 'change_mode',
                pendingSource: data.source || null,
                pendingFile: null,
                pendingFields: null
            };
            this.showConfirmDialog = true;
        },
        onUploadNeedsConfirmation(data) {
            this.confirmData = {
                rowCount: data.currentRowCount,
                action: data.action || 'upload',
                pendingSource: null,
                pendingFile: data.file || null,
                pendingFields: data.fields || null
            };
            this.showConfirmDialog = true;
        },
        async onChangeSource() {
            const rowCount = this.projectData.rowCount || this.projectData.values?.length || 0;

            this.confirmData = {
                rowCount,
                action: 'change_mode',
                pendingSource: null,
                pendingFile: null,
                pendingFields: null
            };

            if (rowCount > 0) {
                this.showConfirmDialog = true;
            } else {
                this.selectedDataSource = null;
                await api.setDataSource(this.project.id, null);
                this.$emit('data-source-changed', null);
                this.clearProjectData();
                this.$emit('source-changed');
            }
        },
        async onConfirmAction(rowCount) {
            this.showConfirmDialog = false;

            if (this.confirmData.action === 'upload' && this.confirmData.pendingFile) {
                try {
                    await api.uploadDataFile(this.project.id, [this.confirmData.pendingFile], rowCount);
                    this.reload();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Успешно',
                        detail: 'Данные загружены',
                        life: 3000
                    });
                    this.onCancelAction();
                } catch (err) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ошибка загрузки',
                        detail: 'Не удалось загрузить файл',
                        life: 5000
                    });
                }
                return;
            }

            if (this.confirmData.action === 'change_keys' && this.confirmData.pendingFields) {
                try {
                    const result = await api.createDataKeys(this.project.id, this.confirmData.pendingFields, rowCount);
                    this.projectData.keys = result.keys || [];
                    this.projectData.values = [];
                    this.projectData.rowCount = 0;
                    this.reload();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Сохранено',
                        detail: 'Структура полей создана',
                        life: 3000
                    });
                    this.onCancelAction();
                } catch (err) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ошибка',
                        detail: 'Не удалось создать структуру полей',
                        life: 5000
                    });
                }
                return;
            }

            if (this.confirmData.action === 'change_mode') {
                if (this.confirmData.pendingSource) {
                    try {
                        await api.setDataSource(this.project.id, this.confirmData.pendingSource, rowCount);
                        this.selectedDataSource = this.confirmData.pendingSource;
                        this.$emit('data-source-changed', this.confirmData.pendingSource);
                        this.loadData();
                        this.onCancelAction();
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Режим изменён',
                            detail: 'Данные были очищены',
                            life: 3000
                        });
                    } catch (err) {
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Ошибка',
                            detail: 'Не удалось изменить режим',
                            life: 5000
                        });
                    }
                } else {
                    this.selectedDataSource = null;
                    await api.setDataSource(this.project.id, null, rowCount);
                    this.$emit('data-source-changed', null);
                    this.clearProjectData();
                    this.onCancelAction();
                    this.$emit('source-changed');
                }
            }
        },
        clearProjectData() {
            this.projectData = {
                keys: [],
                values: [],
                apiKey: this.projectData.apiKey,
                rowCount: 0
            };
        },
        onCancelAction() {
            this.showConfirmDialog = false;
            this.confirmData = {
                rowCount: 0,
                action: 'upload',
                pendingSource: null,
                pendingFile: null,
                pendingFields: null
            };
        }
    }
}
</script>

<style scoped>
.project-data-manager {
    padding: 16px;
    min-height: 400px;
}

.loading-state {
    min-height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #6b7280;
}

.loading-icon {
    font-size: 2rem;
    color: #3b82f6;
}

.loading-text {
    font-size: 0.95rem;
}
</style>
